import {
  Error,
  Paging,
} from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { SummaryInfo } from "app/based/baseApiModel/baseApiResponseModel/ListBaseApiResponseModel";
import { BaseFilterListRequestModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListRequestModel";
import { BaseFilterListResponseModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListResponseModel";
import { BaseUserRequestModel } from "app/based/baseApiModel/baseUserModel/BaseUserRequestModel";
import { BaseUserResponseModel } from "app/based/baseApiModel/baseUserModel/BaseUserResponseModel";
import { BaseViewModel } from "app/based/baseViewModel/BaseViewModel";
import dayjs from "dayjs";
import { TravelSimRegistrationRequestModel } from "features/travelSimRegistration/data/model/TravelSimRegistrationRequestModel";
import { TravelSimRegistrationResponseModel } from "features/travelSimRegistration/data/model/TravelSimRegistrationResponseModel";
import { TravelSimRegistrationInjection } from "features/travelSimRegistration/di/TravelSimRegistrationInjection";
import {
  ExportPayload,
  FetchListInput,
} from "features/travelSimRegistration/domain/entities/TravelSimRegistrationPayload";
import { TravelSimRegistrationUseCase } from "features/travelSimRegistration/domain/useCases/TravelSimRegistrationUseCase";
import { isEmpty } from "lodash";
import { BehaviorSubject } from "rxjs";
import { injectable } from "tsyringe";
import { productCache } from "utils/cache/productCache";
import { CacheKeyEnum } from "utils/enums/CacheKeyEnum";
import { getMessage } from "utils/helpersInTs/helpersInTs";
import { strings } from "utils/localizedStrings";

export interface IDropdownActionRecord {
  record: TravelSimRegistrationResponseModel;
  action: string;
}

export type SavedDetailActionType =
  | TravelSimRegistrationResponseModel
  | IDropdownActionRecord;

class OutboundRegistrationInput implements Disposable {
  public loading = new BehaviorSubject<boolean>(false);
  public detailLoading = new BehaviorSubject<boolean>(false);
  public statusLoading = new BehaviorSubject<Record<string, boolean>>({});
  public fetchList = new BehaviorSubject<FetchListInput>({
    kind: "params",
    params: new TravelSimRegistrationRequestModel(),
  });
  public fetchCarrier = new BehaviorSubject<BaseFilterListRequestModel>({});
  public exportExcel = new BehaviorSubject<ExportPayload | null>(null);
  public fetchNation = new BehaviorSubject<BaseFilterListRequestModel>({});
  public fetchPackage = new BehaviorSubject<BaseFilterListRequestModel>({});
  public fetchDetail = new BehaviorSubject<TravelSimRegistrationRequestModel>(
    new TravelSimRegistrationRequestModel(),
  );
  public fetchPartners = new BehaviorSubject<BaseFilterListRequestModel>({});
  public loadStores = new BehaviorSubject<BaseFilterListRequestModel>({});
  public loadStaff = new BehaviorSubject<BaseUserRequestModel>({});
  public loadUsers = new BehaviorSubject<BaseUserRequestModel>({});

  [Symbol.dispose](): void {
    this.loading.complete();
    this.detailLoading.complete();
    this.fetchList.complete();
    this.fetchNation.complete();
    this.exportExcel.complete();
    this.fetchDetail.complete();
    this.fetchPackage.complete();
    this.fetchPartners.complete();
    this.loadStores.complete();
    this.loadStaff.complete();
    this.loadUsers.complete();
  }
}

class OutboundRegistrationOuput implements Disposable {
  public savedList = new BehaviorSubject<
    Array<TravelSimRegistrationResponseModel>
  >([]);
  public savedCarrier = new BehaviorSubject<Array<BaseFilterListResponseModel>>(
    [],
  );
  public savedDetail = new BehaviorSubject<SavedDetailActionType>({});
  public savedPackage = new BehaviorSubject<Array<BaseFilterListResponseModel>>(
    [],
  );
  public savedNation = new BehaviorSubject<Array<BaseFilterListResponseModel>>(
    [],
  );
  public pagination = new BehaviorSubject<Paging>({});
  public savedSummary = new BehaviorSubject<SummaryInfo>({});
  public onError = new BehaviorSubject<Error>({});
  public onExportSuccess = new BehaviorSubject<boolean>(false);
  public savedPartners = new BehaviorSubject<
    Array<BaseFilterListResponseModel>
  >([]);
  public savedStores = new BehaviorSubject<Array<BaseFilterListResponseModel>>(
    [],
  );
  public savedStaff = new BehaviorSubject<Array<BaseUserResponseModel>>([]);
  public savedUsers = new BehaviorSubject<Array<BaseUserResponseModel>>([]);
  public savedQueryParams =
    new BehaviorSubject<TravelSimRegistrationRequestModel | null>(null);

  [Symbol.dispose](): void {
    this.savedList.complete();
    this.savedDetail.complete();
    this.savedNation.complete();
    this.savedPackage.complete();
    this.pagination.complete();
    this.savedSummary.complete();
    this.onError.complete();
    this.onExportSuccess.complete();
    this.savedPartners.complete();
    this.savedStores.complete();
    this.savedStaff.complete();
    this.savedUsers.complete();
    this.savedQueryParams.complete();
  }
}

@injectable()
export class OutboundRegistrationViewModel extends BaseViewModel<
  OutboundRegistrationInput,
  OutboundRegistrationOuput
> {
  private travelSimRegistrationUseCase: TravelSimRegistrationUseCase;

  constructor() {
    super(new OutboundRegistrationInput(), new OutboundRegistrationOuput());
    this.travelSimRegistrationUseCase =
      TravelSimRegistrationInjection.getTravelSimRegistrationUseCase();
  }

  private downloadExcel(data: Blob) {
    const url = window.URL.createObjectURL(data as Blob);
    const link = document.createElement("a");
    const timestamp = dayjs().format("YYYYMMDD_HHmmss");
    link.href = url;
    link.setAttribute("download", `HELLO_VIETNAM_SIM_REPORT_${timestamp}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  protected override transform(): void {
    this.skipFirstInitial(
      this.input.fetchList,
      async (input: FetchListInput, guard) => {
        try {
          guard?.runIfLatest(() => this.input.loading.next(true));
          let res;
          let paramsToSave: TravelSimRegistrationRequestModel | null = null;

          switch (input.kind) {
            case "search": {
              const result =
                await this.travelSimRegistrationUseCase.getOutboundListFromSearch(
                  input.rawFormValues,
                  input.context,
                );
              res = result.list;
              paramsToSave = result.params;
              break;
            }
            case "tableChange": {
              const result =
                await this.travelSimRegistrationUseCase.getOutboundListWithTableChange(
                  input.prevParams,
                  input.rawFormValues,
                  input.page,
                  input.limit,
                );
              res = result.list;
              paramsToSave = result.params;
              break;
            }
            case "redo": {
              const result =
                await this.travelSimRegistrationUseCase.getOutboundListRedo();
              res = result.list;
              paramsToSave = result.params;
              break;
            }
            case "params":
            default:
              res = await this.travelSimRegistrationUseCase.getOutboundList(
                input.params,
              );
              break;
          }

          if (paramsToSave) {
            this.output.savedQueryParams.next(paramsToSave);
          }

          if (res?.data) {
            this.output.savedList.next(res?.data ?? []);
            this.output.pagination.next(res?.pagination);
            this.output.savedSummary.next({
              error: res?.pagination?.error,
              finished: res?.pagination?.success,
              other: res?.pagination?.other,
              revenue: {
                USD: 0,
                VND: parseInt(res?.pagination?.revenue as string),
              },
            });
          }
        } catch (error) {
          console.error(error);
          this.output.savedList.next([]);
          this.output.onError.next(error);
        } finally {
          guard?.runIfLatest(() => this.input.loading.next(false));
        }
      },
    );

    this.skipFirstInitial(this.input.fetchNation, async (params) => {
      try {
        const res = await this.travelSimRegistrationUseCase.getFilterList(
          params,
          "nation",
        );
        if (res) {
          this.output.savedNation.next(res ?? []);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      }
    });

    this.skipFirstInitial(this.input.fetchPackage, async (params) => {
      const cache = productCache?.getCache(CacheKeyEnum.inboundPakcage);
      if (cache?.length) {
        this.output.savedPackage.next(cache);
      } else {
        try {
          const res = await this.travelSimRegistrationUseCase.getFilterList(
            params,
            "package",
          );
          if (res) {
            this.output.savedPackage.next(res);
            productCache?.saveCache({
              key: CacheKeyEnum.inboundPakcage,
              product: res,
            });
          }
        } catch (error) {
          console.error(error);
          this.output.onError.next(error);
        }
      }
    });

    this.skipFirstInitial(this.input.fetchCarrier, async (params) => {
      try {
        const res = { data: [] };
        if (res?.data) {
          const finalData = res?.data?.filter(
            (child) =>
              child?.code === "viettel" ||
              child?.code === "mobifone" ||
              child?.code === "vinaphone",
          );
          this.output.savedCarrier.next(finalData);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      }
    });

    this.skipFirstInitial(this.input.fetchDetail, async (params) => {
      try {
        this.input.detailLoading.next(true);
        const res = await this.travelSimRegistrationUseCase.getDetail(params);
        if (res?.data) {
          this.output.savedDetail.next(res?.data);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      } finally {
        this.input.detailLoading.next(false);
      }
    });

    this.skipFirstInitial(this.input.exportExcel, async (payload, guard) => {
      if (!payload) return;
      try {
        guard?.runIfLatest(() => this.input.loading.next(true));
        const res =
          await this.travelSimRegistrationUseCase.exportOutboundReportFromSearch(
            payload,
          );
        if (res?.data) {
          if (res?.data?.size < 1000) {
            getMessage(strings.GlobalMessage.ExcelError, 3, "error");
            return;
          }
          this.downloadExcel(res.data);
          this.output.onExportSuccess.next(true);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      } finally {
        guard?.runIfLatest(() => this.input.loading.next(false));
      }
    });

    this.skipFirstInitial(this.input.fetchPartners, async (params) => {
      try {
        const cache = productCache.getCache(CacheKeyEnum.partnerList);

        if (!isEmpty(cache)) {
          this.output.savedPartners.next(cache);
          return;
        }
        const res = await this.travelSimRegistrationUseCase.getFilterList(
          params,
          "partner",
        );
        if (res) {
          const data = res ?? [];
          this.output.savedPartners.next(data);
          productCache.saveCache({
            key: CacheKeyEnum.partnerList,
            product: data,
          });
        }
      } catch (error) {
        console.error("Error fetching partners:", error);
        this.output.savedPartners.next([]);
        this.output.onError.next(error as Error);
      }
    });

    this.skipFirstInitial(this.input.loadStores, async (params) => {
      const { partnerCode } = params;
      const cacheKey = `${CacheKeyEnum.storeList}-${partnerCode}`;
      const cache = productCache?.getCache(cacheKey);
      if (cache?.length) {
        this.output.savedStores.next(cache);
        return;
      }
      try {
        if (partnerCode) {
          const res = await this.travelSimRegistrationUseCase.getFilterList(
            params,
            "store",
          );
          this.output.savedStores.next(res);

          productCache.saveCache({
            key: cacheKey,
            product: res,
          });
          return;
        }
      } catch (error) {
        this.output.onError.next(error);
      }
    });

    this.skipFirstInitial(this.input.loadStaff, async (params) => {
      if (isEmpty(params?.storeCode)) {
        this.output.savedStaff.next([]);
        return;
      }
      try {
        const res = await this.travelSimRegistrationUseCase.getUserForFilter(
          params,
          "staff",
        );
        if (res) {
          const raw = res ?? [];
          const allUsers = raw.flatMap((item: any) => item?.users ?? []);
          const mappedUsers: BaseUserResponseModel[] = allUsers.map(
            (u: any) => {
              return {
                id: u.id,
                name: u.name || u.userName || u.email || u.phone || "",
                code: u.code,
              };
            },
          );
          this.output.savedStaff.next(mappedUsers);
        }
      } catch (e) {
        this.output.savedStaff.next([]);
        this.output.onError.next(e as Error);
      }
    });

    this.skipFirstInitial(this.input.loadUsers, async (params) => {
      try {
        const res = await this.travelSimRegistrationUseCase.getUserForFilter(
          params,
          "userB2B",
        );
        this.output.savedUsers.next(res ?? []);
      } catch (error) {
        this.output.onError.next(error);
        this.output.savedUsers.next([]);
      }
    });
  }
}
